/**
 * MCP Tools for Companies Management
 * 
 * These tools allow MCP clients to interact with companies/firms
 */

import { MCPTool } from '../types';
import { companiesService } from '../../services/companiesService';
import { Company } from '../../types';
import { getMCPServer } from '../server/MCPServer';

// ============================================================================
// CREATE COMPANY
// ============================================================================

export const createCompanyTool: MCPTool = {
  name: 'create_company',
  description: 'Creates a new company/firm in CRM. Use when user says: "Erstelle Firma", "Neue Company", "Füge Unternehmen hinzu". Returns the created company with ID.',
  
  inputSchema: {
    type: 'object',
    properties: {
      name: {
        type: 'string',
        description: 'Company name (required)'
      },
      industry: {
        type: 'string',
        description: 'Industry sector (e.g., "Technology", "Finance", "Healthcare")'
      },
      website: {
        type: 'string',
        description: 'Company website URL'
      },
      phone: {
        type: 'string',
        description: 'Main phone number'
      },
      email: {
        type: 'string',
        description: 'Main email address'
      },
      address: {
        type: 'string',
        description: 'Physical address'
      },
      employeeCount: {
        type: 'number',
        description: 'Number of employees'
      },
      revenue: {
        type: 'number',
        description: 'Annual revenue'
      },
      description: {
        type: 'string',
        description: 'Company description or notes'
      },
      tags: {
        type: 'array',
        items: { type: 'string' },
        description: 'Tags to categorize the company'
      },
      isCustomer: {
        type: 'boolean',
        description: 'Whether this is an existing customer (default: false)'
      }
    },
    required: ['name']
  },

  async execute(params, context) {
    const company: Omit<Company, 'id'> = {
      name: params.name,
      industry: params.industry || '',
      website: params.website || '',
      phone: params.phone || '',
      email: params.email || '',
      address: params.address || '',
      employeeCount: params.employeeCount,
      revenue: params.revenue,
      description: params.description || '',
      tags: params.tags || [],
      isCustomer: params.isCustomer || false,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: context.userId,
      orgId: context.orgId
    };

    const created = await companiesService.createCompany(company);

    return {
      success: true,
      company: created,
      message: `Company "${created.name}" created successfully`
    };
  }
};

// ============================================================================
// LIST COMPANIES
// ============================================================================

export const listCompaniesTool: MCPTool = {
  name: 'list_companies',
  description: 'Lists ALL companies/firms. Use when user asks: "Welche Firmen?", "Zeig alle Companies", "Liste Unternehmen", "Zeig mir Kunden". Can filter by customer status, industry. Returns array of companies.',
  
  inputSchema: {
    type: 'object',
    properties: {
      isCustomer: {
        type: 'boolean',
        description: 'Filter for existing customers only'
      },
      industry: {
        type: 'string',
        description: 'Filter by industry sector'
      },
      minRevenue: {
        type: 'number',
        description: 'Minimum annual revenue'
      },
      limit: {
        type: 'number',
        description: 'Maximum number to return (default: 50)'
      }
    }
  },

  async execute(params, context) {
    let companies = await companiesService.getCompanies(context.orgId);

    // Apply filters
    if (params.isCustomer !== undefined) {
      companies = companies.filter(c => c.isCustomer === params.isCustomer);
    }

    if (params.industry) {
      companies = companies.filter(c => 
        c.industry?.toLowerCase().includes(params.industry.toLowerCase())
      );
    }

    if (params.minRevenue !== undefined) {
      companies = companies.filter(c => (c.revenue || 0) >= params.minRevenue);
    }

    // Apply limit
    const limit = params.limit || 50;
    companies = companies.slice(0, limit);

    return {
      success: true,
      companies,
      count: companies.length,
      customerCount: companies.filter(c => c.isCustomer).length,
      hasMore: companies.length === limit
    };
  }
};

// ============================================================================
// GET COMPANY
// ============================================================================

export const getCompanyTool: MCPTool = {
  name: 'get_company',
  description: 'Retrieves a specific company by ID. Use when user asks for details about a specific company. Example: "Zeig mir Firma Microsoft", "Details zu Company X".',
  
  inputSchema: {
    type: 'object',
    properties: {
      companyId: {
        type: 'string',
        description: 'Unique identifier of the company'
      }
    },
    required: ['companyId']
  },

  async execute(params, context) {
    const company = await companiesService.getCompany(params.companyId);

    if (!company) {
      return {
        success: false,
        error: `Company with ID ${params.companyId} not found`
      };
    }

    if (company.orgId !== context.orgId) {
      return {
        success: false,
        error: 'Access denied: Company belongs to different organization'
      };
    }

    return {
      success: true,
      company
    };
  }
};

// ============================================================================
// UPDATE COMPANY
// ============================================================================

export const updateCompanyTool: MCPTool = {
  name: 'update_company',
  description: 'Updates an existing company. Use when user says: "Ändere Firma", "Update Company Info", "Markiere als Kunde". Can update any company field.',
  
  inputSchema: {
    type: 'object',
    properties: {
      companyId: {
        type: 'string',
        description: 'Company ID to update'
      },
      name: {
        type: 'string',
        description: 'Updated company name'
      },
      industry: {
        type: 'string',
        description: 'Updated industry'
      },
      website: {
        type: 'string',
        description: 'Updated website'
      },
      phone: {
        type: 'string',
        description: 'Updated phone'
      },
      email: {
        type: 'string',
        description: 'Updated email'
      },
      address: {
        type: 'string',
        description: 'Updated address'
      },
      employeeCount: {
        type: 'number',
        description: 'Updated employee count'
      },
      revenue: {
        type: 'number',
        description: 'Updated revenue'
      },
      description: {
        type: 'string',
        description: 'Updated description'
      },
      isCustomer: {
        type: 'boolean',
        description: 'Mark as customer or not'
      },
      tags: {
        type: 'array',
        items: { type: 'string' },
        description: 'Updated tags'
      }
    },
    required: ['companyId']
  },

  async execute(params, context) {
    const { companyId, ...updates } = params;

    // Verify company exists
    const existing = await companiesService.getCompany(companyId);
    
    if (!existing) {
      return {
        success: false,
        error: `Company with ID ${companyId} not found`
      };
    }

    if (existing.orgId !== context.orgId) {
      return {
        success: false,
        error: 'Access denied: Company belongs to different organization'
      };
    }

    // Update
    await companiesService.updateCompany(companyId, {
      ...updates,
      updatedAt: new Date()
    });

    const updated = await companiesService.getCompany(companyId);

    return {
      success: true,
      company: updated,
      message: `Company "${updated?.name}" updated successfully`
    };
  }
};

// ============================================================================
// SEARCH COMPANIES
// ============================================================================

export const searchCompaniesTool: MCPTool = {
  name: 'search_companies',
  description: 'Searches companies using natural language query. Use when user asks: "Suche Firma", "Finde Unternehmen mit X", "Welche Companies mit Tech?". Searches across name, industry, description, tags. Returns matching companies.',
  
  inputSchema: {
    type: 'object',
    properties: {
      query: {
        type: 'string',
        description: 'Search query'
      },
      limit: {
        type: 'number',
        description: 'Maximum results (default: 20)'
      }
    },
    required: ['query']
  },

  async execute(params, context) {
    const companies = await companiesService.getCompanies(context.orgId);
    const queryLower = params.query.toLowerCase();
    
    const matched = companies
      .filter(company => {
        return (
          company.name?.toLowerCase().includes(queryLower) ||
          company.industry?.toLowerCase().includes(queryLower) ||
          company.description?.toLowerCase().includes(queryLower) ||
          company.tags?.some(tag => tag.toLowerCase().includes(queryLower)) ||
          company.website?.toLowerCase().includes(queryLower)
        );
      })
      .slice(0, params.limit || 20);

    return {
      success: true,
      companies: matched,
      count: matched.length,
      customerCount: matched.filter(c => c.isCustomer).length,
      query: params.query
    };
  }
};

// ============================================================================
// REGISTER ALL COMPANY TOOLS
// ============================================================================

export function registerCompanyTools(): void {
  const server = getMCPServer();
  
  server.registerTool(createCompanyTool, {
    category: 'companies',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(listCompaniesTool, {
    category: 'companies',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(getCompanyTool, {
    category: 'companies',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(updateCompanyTool, {
    category: 'companies',
    requiresAuth: true,
    dangerLevel: 'moderate'
  });
  
  server.registerTool(searchCompaniesTool, {
    category: 'companies',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  console.log('✅ Registered 5 company management tools');
}

